import { useRef } from 'react';
import PropTypes from 'prop-types';
import { SnackbarProvider } from 'notistack';
import { Box, IconButton } from 'mui/core';
import { styled } from '@mui/material/styles';
import { Close as CloseIcon, CheckCircleOutline as CheckCircleOutlineIcon, ErrorOutline as ErrorOutlineIcon, ReportProblemOutlined as WarningAmberIcon, InfoOutlined as InfoOutlinedIcon, } from 'mui/icons';
import cx from 'classnames';
import { SNACKBAR_VARIANTS } from 'redux_store';

const PREFIX = 'NotificationsProvider';

const classes = {
  snackbarRoot: `${PREFIX}-snackbarRoot`,
  snackbarType: `${PREFIX}-snackbarType`,
  snackbarSuccess: `${PREFIX}-snackbarSuccess`,
  snackbarError: `${PREFIX}-snackbarError`,
  snackbarWarning: `${PREFIX}-snackbarWarning`,
  snackbarInfo: `${PREFIX}-snackbarInfo`,
  snackbarContainer: `${PREFIX}-snackbarContainer`,
  collapseWrapper: `${PREFIX}-collapseWrapper`,
  iconVariant: `${PREFIX}-iconVariant`,
  successIcon: `${PREFIX}-successIcon`,
  errorIcon: `${PREFIX}-errorIcon`,
  warningIcon: `${PREFIX}-warningIcon`,
  infoIcon: `${PREFIX}-infoIcon`,
  closeButton: `${PREFIX}-closeButton`,
  closeIcon: `${PREFIX}-closeIcon`,
};

const Root = styled('div', {
  name: PREFIX,
})({
  display: 'contents',
  [`& .${classes.snackbarRoot}`]: {
    width: 'fit-content',
    maxWidth: 480,
    minWidth: 320,
    whiteSpace: 'pre-line',
  },
  [`& .${classes.snackbarType}`]: {
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.5,
    padding: '12px 16px !important',
    borderRadius: '10px !important',
    boxShadow: 'var(--qhs-sh-4) !important',
    // Tokens, not `palette.common.white` / `colors.navBg`: those two are
    // fixed values in BOTH themes, which is why every notification came
    // up white on the dark theme. A hairline is added because a surface
    // panel floating over a surface page needs an edge to read as
    // separate once the shadow stops doing that work on a dark page.
    backgroundColor: 'var(--qhs-surface) !important',
    color: 'var(--qhs-text-1) !important',
    border: '1px solid var(--qhs-border) !important',
    flexWrap: 'nowrap !important',
    minWidth: '320px !important',
    maxWidth: '480px !important',
    '& .MuiSnackbarContent-message': {
      padding: '0 !important',
      display: 'flex !important',
      alignItems: 'center',
      flex: 1,
    },
    '& .MuiSnackbarContent-action': {
      marginRight: 0,
      marginLeft: 12,
      paddingLeft: 0,
      alignSelf: 'center',
    },
  },
  [`& .${classes.snackbarSuccess}`]: {},
  [`& .${classes.snackbarError}`]: {},
  [`& .${classes.snackbarWarning}`]: {},
  [`& .${classes.snackbarInfo}`]: {},
  [`& .${classes.snackbarContainer}`]: {
    top: 76,
    pointerEvents: 'none',
    '& > *': {
      pointerEvents: 'all',
    },
  },
  [`& .${classes.collapseWrapper}`]: {
    margin: 0,
  },
  [`& .${classes.iconVariant}`]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginRight: 10,
    '& svg': {
      fontSize: 22,
    },
  },
  [`& .${classes.successIcon}`]: {
    color: 'var(--qhs-success)',
  },
  [`& .${classes.errorIcon}`]: {
    color: 'var(--qhs-error)',
  },
  [`& .${classes.warningIcon}`]: {
    color: 'var(--qhs-warning)',
  },
  [`& .${classes.infoIcon}`]: {
    color: 'var(--qhs-info)',
  },
  [`& .${classes.closeButton}`]: {
    color: 'var(--qhs-text-3)',
    padding: 4,
    transition: 'color 0.2s ease',
    '&:hover': {
      color: 'var(--qhs-text-1)',
      backgroundColor: 'transparent',
    },
  },
  [`& .${classes.closeIcon}`]: {
    width: 18,
    height: 18,
  },
});

const SUCCESS_DURATION = 5000;
export const INFO_DURATION = 5000;
export const ERROR_DURATION = 10000;
export const WARNING_DURATION = 10000;

const SNACKBAR_OPTIONS = {
  maxSnack: 3,
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'center',
  },
  autoHideDuration: SUCCESS_DURATION,
};

function NotificationsProvider({ children }) {
  const providerRef = useRef();

  const onClickDismiss = (key) => () => {
    providerRef.current.closeSnackbar(key);
  };

  return (
    <Root>
      <SnackbarProvider
        ref={providerRef}
        {...SNACKBAR_OPTIONS}
        classes={{
          variantSuccess: cx(classes.snackbarType, classes.snackbarSuccess),
          variantError: cx(classes.snackbarType, classes.snackbarError),
          variantWarning: cx(classes.snackbarType, classes.snackbarWarning),
          variantInfo: cx(classes.snackbarType, classes.snackbarInfo),
          root: classes.snackbarRoot,
          containerAnchorOriginBottomCenter: classes.snackbarContainer,
          collapseWrapper: classes.collapseWrapper,
        }}
        iconVariant={{
          [SNACKBAR_VARIANTS.success]: (
            <Box className={cx(classes.iconVariant, classes.successIcon)}>
              <CheckCircleOutlineIcon />
            </Box>
          ),
          [SNACKBAR_VARIANTS.error]: (
            <Box className={cx(classes.iconVariant, classes.errorIcon)}>
              <ErrorOutlineIcon />
            </Box>
          ),
          [SNACKBAR_VARIANTS.warning]: (
            <Box className={cx(classes.iconVariant, classes.warningIcon)}>
              <WarningAmberIcon />
            </Box>
          ),
          [SNACKBAR_VARIANTS.info]: (
            <Box className={cx(classes.iconVariant, classes.infoIcon)}>
              <InfoOutlinedIcon />
            </Box>
          ),
        }}
        action={(key) => (
          <IconButton
            onClick={onClickDismiss(key)}
            size="small"
            className={classes.closeButton}
          >
            <CloseIcon className={classes.closeIcon} />
          </IconButton>
        )}
      >
        {children}
      </SnackbarProvider>
    </Root>
  );
}

NotificationsProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

export default NotificationsProvider;
